/** @prettier */

/**
 * Phone Numbers, for our purposes, are generally divided into two segments:
 * - the international country code
 * - the national/subscriber phone (includes the area code and customer's phone number)
 *
 * This is based on the following schemes:
 * * NANP - North American Numbering Plan
 * * E.164 - An international standard (recommenedation) for PSTN (Public
 *           Telecommunication Numbering Plan)
 *
 * These are merely RECOMMENDATIONS and how phone numbers are implemented
 * varies.
 *
 * Phone Numbers are actually a lot more varied and complicated.
 * One should refer to `Phone.java` in the `linemonkey` repository.
 *
 * See also:
 * * <https://en.wikipedia.org/wiki/E.164>
 * * <https://www.twilio.com/docs/glossary/what-e164>
 * * <https://en.wikipedia.org/wiki/North_American_Numbering_Plan>
 * * <https://github.com/google/libphonenumber/blob/master/FALSEHOODS.md>
 */

var NANP_COUNTRY_CODE_PREFIX = "1";
var E164_INTERNATIONAL_PREFIX = "+";

// NOTE: Most countries use 0, 00, 010, or 011. HOWEVER, some countries use
// numbers or combinations
var COMMON_INTERNATIONAL_DIALING_PREFIX = "0";

var E164_COUNTRY_CODE_MAX_LEN = 3;
var NANP_NUMBER_MAX_LEN = 11;

/**
 *
 * @param {string} number
 * @returns {boolean}
 */
function hasNorthAmericanNumberingPlanPrefix(number) {
  return (number) ? NANP_COUNTRY_CODE_PREFIX === number.charAt(0) : false;
}

/**
 *
 * @param {string} number
 * @returns {boolean}
 */
function hasE164InternationalPrefix(number) {
  return (number) ? E164_INTERNATIONAL_PREFIX === number.charAt(0) : false;
}

/**
 *
 * @param {string} number
 * @returns {boolean}
 */
function isInternationalDialing(number) {
  return (number) ? COMMON_INTERNATIONAL_DIALING_PREFIX === number.charAt(0) : false;
}

/**
 *
 * @param {string} number
 * @returns
 */
function normaliseTelephoneNumber(number) {
  return (number) ? number.replace(/[^\d]/g, "") : "";
}

/**
 *
 * @param {string} number a complete phone number, preferably with a country code prefix
 * @returns {boolean}
 */
function isValidTelephoneNumber(number) {
  var normalisedNumber = normaliseTelephoneNumber(number);
  var hasNANPPrefix = hasNorthAmericanNumberingPlanPrefix(number)

  return (
    (hasNANPPrefix && normalisedNumber.length === NANP_NUMBER_MAX_LEN) 
    || (hasE164InternationalPrefix(number) && normalisedNumber.length >= E164_COUNTRY_CODE_MAX_LEN) 
    // If the number begins with 0, there is a lot of variance and it may be reasonable to assume 
    // if the number is longer than a country code, we can send it to the server and let the server
    // deal with it if it's invalid 
    || (isInternationalDialing(number) && normalisedNumber.length >= E164_COUNTRY_CODE_MAX_LEN)
    || (!hasNANPPrefix && normalisedNumber.length > 9)
  );
}

/**
 * 
 * @param {string|number} countryCode 
 * @param {string|number} number 
 * @returns  {string}
 */
function prefixWithCountryCode(countryCode, number) {
  var normalisedNumber = normaliseTelephoneNumber(number)
  var result = normalisedNumber

  if (countryCode) {
    if (normalisedNumber.length <= 10) {
      result = '+' + countryCode + normalisedNumber;
    }
    else if (normalisedNumber.charAt(0) === '0') {
      result = '+' + countryCode + normalisedNumber.substring(1)
    }
  }

  return result;
}

module.exports = {
  isValidTelephoneNumber: isValidTelephoneNumber,
  normaliseTelephoneNumber: normaliseTelephoneNumber,
  prefixWithCountryCode: prefixWithCountryCode
}
